import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
	Home, 
	Users, 
	Briefcase, 
	ClipboardList, 
	Puzzle, 
	CalendarDays, 
	CheckSquare, 
	Settings,
	Shield, // Replacing AdminPanelSettings with Shield icon
	Beaker // Adding Beaker icon for Match Test
} from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/providers/SettingsProvider";

interface NavigationProps {
	collapsed: boolean;
	orientation?: "vertical" | "horizontal";
}

export const Navigation = ({ collapsed, orientation = "vertical" }: NavigationProps) => {
	
	const { isSuperAdmin, isAdmin } = useAuth();
	const { t } = useTranslation();
	const { devMode } = useSettings();
	
	const navItems = [
		{ title: t('navigation.dashboard'), icon: Home, path: "/" },
		{ title: t('navigation.candidates'), icon: Users, path: "/candidates" },
		{ title: t('navigation.companies'), icon: Briefcase, path: "/companies" },
		{ title: t('navigation.jobs'), icon: ClipboardList, path: "/jobs" },
		{ title: t('navigation.matches'), icon: Puzzle, path: "/matches" },
		{ title: t('navigation.kanban'), icon: CheckSquare, path: "/kanban" },
		{ title: t('navigation.calendar'), icon: CalendarDays, path: "/calendar" },
		{ title: t('navigation.settings'), icon: Settings, path: "/settings" },
		// Only show admin panel for super admins
		...(isSuperAdmin || isAdmin ? [{ title: t('navigation.admin'), icon: Shield, path: "/admin" }] : []),
		// Only show Match Test when dev mode is enabled
		...(devMode ? [{ title: "Match Test", icon: Beaker, path: "/match-test" }] : []),
	];

	return (
		<nav className={cn(
			"flex-1 overflow-y-auto py-6",
			orientation === "horizontal" && "flex-1 flex items-center justify-center py-0"
		)}>
			<ul className={cn(
				"space-y-1 px-3",
				orientation === "horizontal" && "flex space-y-0 space-x-1 px-0"
			)}>
				{navItems.map((item) => (
					<li key={item.title} className={cn(
						orientation === "horizontal" && "flex-1"
					)}>
						<NavLink
							to={item.path}
							className={({ isActive }) => cn(
								"sidebar-item",
								isActive && "sidebar-item-active",
								orientation === "horizontal" && "flex-col items-center justify-center px-1 py-1 text-xs gap-1"
							)}
							title={item.title}
						>
							<item.icon size={orientation === "horizontal" ? 18 : 20} />
							{!collapsed && orientation !== "horizontal" && <span>{item.title}</span>}
							{orientation === "horizontal" && <span className="hidden sm:block truncate">{item.title}</span>}
						</NavLink>
					</li>
				))}
			</ul>
		</nav>
	);
};
